// useArtifactVersions — React Query hooks för artifact + artifact_version.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { callRpc } from '@/lib/supabaseTyped';
import type { WorkflowState } from '@/types/workflow';
import type { Intent } from '@/lib/intentTypes';

export interface ArtifactRow {
  id: string;
  title: string;
  current_version_id: string | null;
  version_count: number;
  created_at: string;
  updated_at: string;
}

export interface ArtifactVersionRow {
  id: string;
  version_number: number;
  parent_version_id: string | null;
  operation: Intent;
  diff_from_parent: unknown;
  prompt_message_id: string | null;
  regression_check_result: unknown;
  label: string | null;
  created_at: string;
}

export function useConversationArtifacts(conversationId: string | null) {
  return useQuery({
    queryKey: ['artifacts', conversationId],
    queryFn: async (): Promise<ArtifactRow[]> => {
      if (!conversationId) return [];
      const { data, error } = await callRpc<ArtifactRow[]>('list_conversation_artifacts', {
        p_conversation_id: conversationId,
      });
      if (error) {
        console.warn('[list_conversation_artifacts] RPC failed', {
          conversationId, message: error.message,
        });
        return [];
      }
      return (data ?? []) as ArtifactRow[];
    },
    enabled: !!conversationId,
    staleTime: 10_000,
    retry: false,
  });
}

export function useArtifactVersions(artifactId: string | null) {
  return useQuery({
    queryKey: ['artifact-versions', artifactId],
    queryFn: async (): Promise<ArtifactVersionRow[]> => {
      if (!artifactId) return [];
      const { data } = await callRpc<ArtifactVersionRow[]>('list_artifact_versions', {
        p_artifact_id: artifactId,
      });
      return (data ?? []) as ArtifactVersionRow[];
    },
    enabled: !!artifactId,
    staleTime: 10_000,
  });
}

export function useArtifactMutations() {
  const qc = useQueryClient();

  const invalidate = useCallback(
    (conversationId: string | null, artifactId?: string) => {
      qc.invalidateQueries({ queryKey: ['artifacts', conversationId] });
      if (artifactId) {
        qc.invalidateQueries({ queryKey: ['artifact-versions', artifactId] });
      }
    },
    [qc],
  );

  const createVersion = useCallback(
    async (input: {
      conversationId: string;
      artifactId: string | null;
      operation: Intent;
      content: WorkflowState;
      diffFromParent?: unknown;
      promptMessageId?: string;
      regressionCheckResult?: unknown;
      title?: string;
    }): Promise<{
      artifactId?: string;
      versionId?: string;
      versionNumber?: number;
      error?: string;
    }> => {
      const { data, error } = await callRpc<{
        success: boolean;
        artifact_id?: string;
        version_id?: string;
        version_number?: number;
        error?: string;
      }>('create_artifact_version', {
        p_conversation_id: input.conversationId,
        p_artifact_id: input.artifactId,
        p_operation: input.operation,
        p_content: input.content as unknown as Record<string, unknown>,
        p_diff_from_parent: input.diffFromParent ?? null,
        p_prompt_message_id: input.promptMessageId ?? null,
        p_regression_check_result: input.regressionCheckResult ?? null,
        p_title: input.title ?? null,
      });
      if (error) return { error: error.message };
      if (!data?.success) return { error: data?.error };
      invalidate(input.conversationId, data.artifact_id);
      return {
        artifactId: data.artifact_id,
        versionId: data.version_id,
        versionNumber: data.version_number,
      };
    },
    [invalidate],
  );

  const setCurrentVersion = useCallback(
    async (input: {
      conversationId: string;
      artifactId: string;
      versionId: string;
    }): Promise<{ success: boolean; error?: string }> => {
      const { data, error } = await callRpc<{ success: boolean; error?: string }>(
        'set_artifact_current_version',
        { p_artifact_id: input.artifactId, p_version_id: input.versionId },
      );
      if (error) return { success: false, error: error.message };
      if (!data?.success) return { success: false, error: data?.error };
      invalidate(input.conversationId, input.artifactId);
      return { success: true };
    },
    [invalidate],
  );

  const getVersionContent = useCallback(
    async (versionId: string): Promise<WorkflowState | null> => {
      const { data } = await callRpc<{
        success: boolean;
        content?: WorkflowState;
        error?: string;
      }>('get_artifact_version_content', { p_version_id: versionId });
      if (!data?.success || !data.content) return null;
      return data.content;
    },
    [],
  );

  return { createVersion, setCurrentVersion, getVersionContent };
}
